import { IOfficeHost } from "@core/shared/office-hosts/office-host.types";
import { logger } from "@services/logger/logger.service";
import * as cmmn from '@utils/index';
import { tableAsText } from "@utils/index";
import { BaseHost } from './base-host';

type IExcelProcess = (context: Excel.RequestContext) => Promise<string | null>;
type IExcelSuccess = (result: string) => void;
type IExcelError = (error: Office.Error) => void;

async function tryCatch(
    onProcess: IExcelProcess,
    success?: IExcelSuccess,
    failed?: IExcelError
): Promise<void>
{
    await cmmn.callAsync<string>(async (r) =>
    {
        let resultValue = '';
        try
        {
            const result = await Excel.run<string | null>(onProcess);
            if (success && result)
            {
                success(result);
            }
            resultValue = result || '';
        } catch (error)
        {
            if (failed)
            {
                failed(error as Office.Error);
            }
        } finally
        {
            r.next(resultValue);
        }
    });
}

export class ExcelHost extends BaseHost implements IOfficeHost
{
    private _isExcelApiSupport = this.isExcelApiSupport();

    public override async getDocumentIDAsync(): Promise<string>
    {
        const fileUrlID = await this.resolveFileUrlDocumentIDAsync();
        if (fileUrlID)
        {
            return fileUrlID;
        }

        return (await this.resolveSettingsDocumentIDAsync()) ?? '';
    }

    public override checkSelectable()
    {
        return true;
    }

    public override checkEditable()
    {
        return true;
    }

    public override checkDocumentReadable()
    {
        return true;
    }

    public override getTag(): string
    {
        return 'excel';
    }

    public override async getDocumentTextAsync(): Promise<string>
    {
        let resultStr = '';
        await tryCatch(async (context) =>
        {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            sheet.load('name');
            await context.sync();

            const usedRange = sheet.getUsedRangeOrNullObject(true);
            usedRange.load('isNullObject');
            await context.sync();

            if (usedRange.isNullObject)
            {
                return null;
            }

            usedRange.load('text');
            await context.sync();

            if (!usedRange.text)
            {
                return null;
            }

            return `${ sheet.name }\n${ tableAsText(usedRange.text) }`;
        }, (result) =>
        {
            logger.debug(this, `getDocumentTextAsync() > success`);
            resultStr = result;
        }, error =>
        {
            logger.error(this, `getDocumentTextAsync() > failed: ${ error.message }`);
        });

        return resultStr;
    }

    public override async getSelectedContentAsync(): Promise<string>
    {
        let resultStr = '';
        await tryCatch(async (context) =>
        {
            const selectedRanges = context.workbook.getSelectedRanges();
            selectedRanges.load('address');
            await context.sync();

            if (!selectedRanges.address)
            {
                return null;
            }

            const texts: string[] = [];

            const activeSheet = context.workbook.worksheets.getActiveWorksheet();
            activeSheet.load('name');
            await context.sync();

            const addresses = selectedRanges.address.split(',');
            for (const address of addresses)
            {
                const addressVal = address.replaceAll(`'`, ``);
                if (addressVal.startsWith(activeSheet.name))
                {
                    const range = activeSheet.getRange(address);
                    if (!this._isExcelApiSupport)
                    {
                        range.load("text");

                        await context.sync();

                        if (range.text)
                        {
                            for (const col of range.text)
                            {
                                for (const cell of col)
                                {
                                    if (cell)
                                    {
                                        texts.push(cell);
                                    }
                                }
                            }
                        }
                        continue;
                    }

                    const usedRange = range.getUsedRangeOrNullObject(true);

                    usedRange.load("address");
                    await context.sync();

                    usedRange.load("text");
                    await context.sync();

                    const values = usedRange.text;
                    if (values)
                    {
                        const contentStr = tableAsText(values);
                        // const data = safeJsonParse(contentStr);
                        texts.push(contentStr);
                    }
                }
            }

            // for (const [index, element] of Object.entries(texts)) {
            //     if (Array.isArray(element)) {
            //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //         // @ts-expect-error
            //         texts[index] = element.map(item => {
            //             if (Array.isArray(item)) {
            //                 return item.filter(data => !!data);
            //             }
            //
            //             return item
            //         })
            //     }
            // }
            return texts.join(',') ?? null;
        }, (result) =>
        {
            logger.debug(this, `getSelectedContentAsync() > success: ${ result }`);
            resultStr = result;
        }, error =>
        {
            logger.error(this, `getSelectedContentAsync() > failed: ${ error.message }`);
        });

        return resultStr;
    }

    public override async setSelectedTextAsync(plainText: string): Promise<void>
    {
        await Excel.run(async (context) =>
        {
            const selectedRange = context.workbook.getSelectedRange();
            if (!selectedRange)
            {
                throw new Error('Selected range not found');
            }
            selectedRange.values = [ [ plainText ] ];

            await context.sync();
        });
    }

    private isExcelApiSupport(): boolean
    {
        try
        {
            return Office.context.requirements.isSetSupported('ExcelApi', '1.4');
        } catch (e)
        {
            logger.error(e);
            return false;
        }
    }
}
